import { useEffect, useMemo, useState } from 'react';
import { type FilterValueReplacement, type RowData } from '@tanstack/react-table';
import { type LegacyColumn as Column, type LegacyRow as Row } from '@tanstack/react-table/legacy';
import { useTranslation } from 'react-i18next';
import { FilterIcon } from '#icons';
import { Checkbox } from '#components/Input/Checkbox';

import { Searchfield } from '#components/Input/Text/Searchfield';
import { IconButton } from '#components/Input/IconButton';
import { useFoldoutMenu } from '../../../../../hooks/useFoldoutMenu/useFoldoutMenu';
import {
  CheckBoxWrapper,
  FilterActiveIndicator,
  FilterContainer,
  FloatingContainer,
  ItemsContainer,
  SearchWrapper,
} from './styles';

const ommitEmptyValues = (value: string | number): boolean => {
  if (!value) {
    return false;
  }

  return typeof value !== 'string' || value.trim() !== '';
};

interface FilterDropDownValues {
  trueValue: string | number;
  label: string | number;
}

export const getUniqueColumnValues = <T extends RowData>(
  columnId: string,
  flatRows: Row<T>[],
  valueReplacement: FilterValueReplacement | undefined,
): FilterDropDownValues[] => {
  const uniqueValues: FilterDropDownValues[] = Array.from(
    new Set<string | number>(flatRows.map((row) => row.getValue(columnId))),
  )
    .filter(ommitEmptyValues)
    .map((value) => ({ trueValue: value, label: value }));

  if (!valueReplacement) {
    return uniqueValues;
  }

  const replacementValueIndex = uniqueValues.findIndex(
    (value) => value.trueValue === valueReplacement.value,
  );

  if (replacementValueIndex === -1) {
    return uniqueValues;
  }

  if (!valueReplacement.prioritize) {
    uniqueValues[replacementValueIndex].label = valueReplacement.replacementValueToDisplay;

    return uniqueValues;
  }

  const [replacementValue] = uniqueValues.splice(replacementValueIndex, 1);

  replacementValue.label = valueReplacement.replacementValueToDisplay;

  return [replacementValue, ...uniqueValues];
};

const SelectableFilter = <T extends RowData, V extends string | number>({
  column,
}: {
  column: Column<T, V>;
}) => {
  const { t } = useTranslation();

  const filterValue = column.getFilterValue() as V[] | undefined;
  const {
    allowFilterSearch = false,
    allowFilterToggleAll = false,
    replaceFilterValue,
    persistFilter = false,
  } = column.columnDef.meta ?? {};

  const rows = column.getFacetedRowModel().flatRows;

  const uniqueColumnValues = useMemo(
    () => getUniqueColumnValues(column.id, rows, replaceFilterValue),
    [column.id, rows, replaceFilterValue],
  );

  useEffect(() => {
    setFilterItemsToShow(uniqueColumnValues);
  }, [uniqueColumnValues]);

  const [selectedFilterValues, setSelectedFilterValues] = useState<V[]>(filterValue ?? []);

  const [filterItemsToShow, setFilterItemsToShow] =
    useState<FilterDropDownValues[]>(uniqueColumnValues);

  const { refs, getReferenceProps, getFloatingProps, floatingStyles, setIsOpen, isOpen } =
    useFoldoutMenu({
      placement: 'bottom-end',
    });

  useEffect(() => {
    if (!persistFilter) {
      // Reset filter when data changes
      setSelectedFilterValues([]);
    }
    setFilterItemsToShow(uniqueColumnValues);

    // "persistFilter" is set at the code level and should not change during app runtime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueColumnValues]);

  const onSearchChange = (value: string) => {
    if (value === '') {
      setFilterItemsToShow(uniqueColumnValues);
    }

    const filteredItems = uniqueColumnValues.filter(({ trueValue }) =>
      trueValue.toString().toLowerCase().includes(value.toLowerCase()),
    );

    setFilterItemsToShow(filteredItems);
  };

  // Initialize Filter Value
  useEffect(() => {
    if (!filterValue) {
      column.setFilterValue(selectedFilterValues);
    }
  }, [selectedFilterValues, column, filterValue]);

  // Keep filter value in sync with selected values
  useEffect(() => {
    column.setFilterValue(selectedFilterValues);
  }, [selectedFilterValues, column]);

  const allValuesAreChecked = useMemo(() => {
    const selectedValuesSet = new Set(selectedFilterValues);

    return uniqueColumnValues.every(({ trueValue }) => selectedValuesSet.has(trueValue as V));
  }, [selectedFilterValues, uniqueColumnValues]);

  const onToggleValue = (toggledValue: V) => {
    if (selectedFilterValues.includes(toggledValue)) {
      setSelectedFilterValues(
        selectedFilterValues.filter((selectedValue) => selectedValue !== toggledValue),
      );
    } else {
      setSelectedFilterValues([...selectedFilterValues, toggledValue]);
    }
  };
  const onToggleAll = () => {
    if (allValuesAreChecked) {
      setSelectedFilterValues([]);
    } else {
      setSelectedFilterValues([...uniqueColumnValues.map(({ trueValue }) => trueValue as V)]);
    }
  };

  const filterActive = filterValue !== undefined && filterValue.length > 0;

  return (
    <FilterContainer>
      <IconButton
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        variant="secondary"
        isActive={isOpen}
        ref={(node) => {
          refs.setReference(node);
        }}
        {...getReferenceProps()}
      >
        <FilterIcon />
        {filterActive && <FilterActiveIndicator />}
      </IconButton>
      {isOpen && (
        <FloatingContainer
          ref={(node) => {
            refs.setFloating(node);
          }}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {allowFilterSearch && (
            <SearchWrapper>
              <Searchfield
                placeholder={t('component.tableV2.filter.search')}
                onChange={onSearchChange}
              />
            </SearchWrapper>
          )}
          <ItemsContainer>
            {allowFilterToggleAll && (
              <CheckBoxWrapper key="all" bottomBorder>
                <Checkbox
                  onChange={() => {
                    onToggleAll();
                  }}
                  checked={allValuesAreChecked}
                  label={t('component.tableV2.filter.all')}
                  multilineLabel={false}
                />
              </CheckBoxWrapper>
            )}
            {filterItemsToShow.map(({ trueValue, label }) => (
              <CheckBoxWrapper key={trueValue}>
                <Checkbox
                  onChange={() => {
                    onToggleValue(trueValue as V);
                  }}
                  fullWidth
                  checked={selectedFilterValues.includes(trueValue as V)}
                  label={typeof label === 'number' ? label.toString() : label}
                  multilineLabel={false}
                />
              </CheckBoxWrapper>
            ))}
          </ItemsContainer>
        </FloatingContainer>
      )}
    </FilterContainer>
  );
};

export default SelectableFilter;
